import { TripComponent, ComponentType } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  PlaneIcon, 
  BuildingIcon, 
  SmileIcon, 
  TruckIcon, 
  UtensilsIcon, 
  FileIcon,
  FileTextIcon,
  XIcon
} from "lucide-react";
import { format } from "date-fns";

interface DetailedViewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  component: TripComponent;
  onDelete: () => void;
  onRegenerate: () => void;
  onEdit?: () => void; // Optional edit handler
}

export default function DetailedViewModal({ 
  component, 
  open,
  onOpenChange,
  onDelete, 
  onRegenerate,
  onEdit
}: DetailedViewModalProps) {
  const getComponentIcon = (type: string) => {
    switch (type) {
      case "flight":
        return { 
          icon: <PlaneIcon className="h-6 w-6" />,
          bgColor: "bg-blue-100",
          textColor: "text-primary" 
        };
      case "hotel":
        return { 
          icon: <BuildingIcon className="h-6 w-6" />,
          bgColor: "bg-orange-100",
          textColor: "text-secondary" 
        };
      case "activity":
        return { 
          icon: <SmileIcon className="h-6 w-6" />,
          bgColor: "bg-green-100",
          textColor: "text-accent" 
        };
      case "transport":
        return { 
          icon: <TruckIcon className="h-6 w-6" />,
          bgColor: "bg-purple-100",
          textColor: "text-purple-600" 
        };
      case "food":
        return { 
          icon: <UtensilsIcon className="h-6 w-6" />,
          bgColor: "bg-red-100",
          textColor: "text-red-600" 
        };
      default:
        return { 
          icon: <FileIcon className="h-6 w-6" />,
          bgColor: "bg-gray-100",
          textColor: "text-gray-600" 
        };
    }
  };

  const { icon, bgColor, textColor } = getComponentIcon(component.type);

  const renderComponentSpecificDetails = () => {
    const details = component.details as any; // Type assertion since details are dynamic
    
    switch (component.type) {
      case "flight":
        return (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Flight Details</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {details?.airline && (
                <div className="flex justify-between">
                  <span className="text-sm">Airline</span>
                  <span className="text-sm font-medium">{details.airline}</span>
                </div>
              )}
              {details?.flightNumber && (
                <div className="flex justify-between">
                  <span className="text-sm">Flight Number</span>
                  <span className="text-sm font-medium">{details.flightNumber}</span>
                </div>
              )}
              {details?.seatNumber && (
                <div className="flex justify-between">
                  <span className="text-sm">Seat</span>
                  <span className="text-sm font-medium">{details.seatNumber}</span>
                </div>
              )}
              {details?.departureAirport && details?.arrivalAirport && (
                <div className="flex justify-between">
                  <span className="text-sm">Route</span>
                  <span className="text-sm font-medium">{details.departureAirport} → {details.arrivalAirport}</span>
                </div>
              )}
              {details?.bookingReference && (
                <div className="flex justify-between">
                  <span className="text-sm">Booking Reference</span>
                  <span className="text-sm font-medium">{details.bookingReference}</span>
                </div>
              )}
            </div>
          </div>
        );
      
      case "hotel":
        return (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Hotel Details</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {details?.confirmationNumber && (
                <div className="flex justify-between">
                  <span className="text-sm">Confirmation Number</span>
                  <span className="text-sm font-medium">{details.confirmationNumber}</span>
                </div>
              )}
              {details?.checkInTime && (
                <div className="flex justify-between">
                  <span className="text-sm">Check-in Time</span>
                  <span className="text-sm font-medium">{details.checkInTime}</span>
                </div>
              )}
              {details?.checkOutTime && (
                <div className="flex justify-between">
                  <span className="text-sm">Check-out Time</span>
                  <span className="text-sm font-medium">{details.checkOutTime}</span>
                </div>
              )}
            </div>
          </div>
        );
        
      case "activity":
        return (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Activity Details</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {details?.activityType && (
                <div className="flex justify-between">
                  <span className="text-sm">Type</span>
                  <span className="text-sm font-medium">{details.activityType}</span>
                </div>
              )}
              {details?.ticketRequired !== undefined && (
                <div className="flex justify-between">
                  <span className="text-sm">Ticket Required</span>
                  <span className="text-sm font-medium">{details.ticketRequired ? "Yes" : "No"}</span>
                </div>
              )}
            </div>
          </div>
        );
        
      case "transport":
        return (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Transport Details</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {details?.transportType && (
                <div className="flex justify-between">
                  <span className="text-sm">Type</span>
                  <span className="text-sm font-medium">{details.transportType}</span>
                </div>
              )}
              {details?.ticketInfo && (
                <div className="flex justify-between">
                  <span className="text-sm">Ticket Info</span>
                  <span className="text-sm font-medium">{details.ticketInfo}</span>
                </div>
              )}
            </div>
          </div>
        );
        
      case "food":
        return (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Food Details</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {details?.cuisine && (
                <div className="flex justify-between">
                  <span className="text-sm">Cuisine</span>
                  <span className="text-sm font-medium">{details.cuisine}</span>
                </div>
              )}
              {details?.reservation !== undefined && (
                <div className="flex justify-between">
                  <span className="text-sm">Reservation</span>
                  <span className="text-sm font-medium">{details.reservation ? "Yes" : "No"}</span>
                </div>
              )}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  const formatComponentTitle = (type: string) => {
    switch (type) {
      case "flight": return "Flight Details";
      case "hotel": return "Hotel Details";
      case "activity": return "Activity Details";
      case "transport": return "Transport Details";
      case "food": return "Meal Details";
      default: return "Item Details";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
        <DialogHeader className="flex flex-row items-center space-x-3">
          <div className={`w-10 h-10 ${bgColor} ${textColor} rounded-full flex items-center justify-center`}>
            {icon}
          </div>
          <div>
            <DialogTitle>{formatComponentTitle(component.type as any)}</DialogTitle>
            <p className="text-sm text-gray-600">{component.title}</p>
          </div>
          <Button
            variant="ghost"
            size="icon" aria-label="Close details"
            className="absolute right-4 top-4"
            onClick={() => onOpenChange(false)}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          <div>
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Time & Date</h4>
              <div className="flex items-center space-x-4">
                <div className="flex-grow">
                  <p className="text-lg font-bold">{component.startTime || "N/A"}</p>
                  <p className="text-sm">{format(new Date(component.startDate), "EEEE, MMMM d, yyyy")}</p>
                  <p className="text-sm font-medium">{component.location}</p>
                </div>
                
                {component.endTime && (
                  <>
                    <div className="flex flex-col items-center px-4">
                      <div className="text-xs text-gray-500 mb-1">Duration</div>
                      <div className="w-20 h-0.5 bg-gray-300 relative">
                        <div className="absolute -top-1.5 right-0 w-3 h-3 transform rotate-45 border-t border-r border-gray-300"></div>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <p className="text-lg font-bold">{component.endTime}</p>
                      <p className="text-sm">
                        {component.endDate 
                          ? format(new Date(component.endDate), "EEEE, MMMM d, yyyy")
                          : format(new Date(component.startDate), "EEEE, MMMM d, yyyy")}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {renderComponentSpecificDetails()}
          </div>
          
          <div>
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Notes</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm">{component.description || "No notes added."}</p>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">Attachments</h4>
              <div className="flex items-center space-x-2">
                <Button variant="outline" className="flex items-center space-x-2">
                  <FileTextIcon className="h-5 w-5 text-gray-500" />
                  <span className="text-sm">Add Attachment</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex justify-between border-t border-border p-4">
          <div className="flex space-x-2">
            <Button 
              variant="ghost" 
              className="text-red-600 hover:bg-red-50 hover:text-red-600"
              onClick={onDelete}
            >
              Delete
            </Button>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {onEdit && (
              <Button 
                variant="outline" 
                className="bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border-blue-200"
                onClick={() => {
                  if (onEdit) {
                    onEdit();
                    onOpenChange(false);
                  }
                }}
              >
                Edit
              </Button>
            )}
            <Button onClick={onRegenerate}>
              Regenerate
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
